/** biome-ignore-all lint/a11y/useSemanticElements: biome does not pickup the "is" prop */

import { LinearCreatePricePlugin } from '@domains/subscription/priv/Billables/factories/LinearPricePluginFactory'
import { MaxCreatePricePlugin } from '@domains/subscription/priv/Billables/factories/MaxPricePluginFactory'
import { MinCreatePricePlugin } from '@domains/subscription/priv/Billables/factories/MinPricePluginFactory'
import { TieredCreatePricePlugin } from '@domains/subscription/priv/Billables/factories/TieredPricePluginFactory'
import { CreatePriceForm } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import type { ConfirmableProps } from '@domains/subscription/priv/components/Confirmable'
import { formatFieldChangeName } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { StringFormatter } from '@domains/subscription/priv/format'
import { Button, Stack, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { type BillablePrice, isUnsupportedBillablePrice, type UnsupportedBillablePrice } from '../../types'
import type { BaseBillable, PriceRowPlugin, SaveCallbackData } from './plugin'

type GenericConfirmableComponent = <T>(props: ConfirmableProps<T>) => React.ReactElement

export interface BillableFormProps<T extends BaseBillable> {
  billableId: string
  billablePrice: T | UnsupportedBillablePrice | undefined
  onCreatedPrice: () => void
  onDelete: (billableId: string) => void
  title: string
  Confirmable?: GenericConfirmableComponent
  templateId: string
  unit: string
  currencies: string[]
  formatPaymentFrequency: (paymentFrequency: BillablePrice['paymentFrequency']) => string
  plugins: PriceRowPlugin[]
  onSuccessSave: () => void
}

type PriceFormState = { open: true; mode: 'edit' | 'create' } | { open: false }

export const BillableForm = <T extends BaseBillable>({
  billableId,
  billablePrice,
  onDelete,
  title,
  Confirmable,
  unit,
  templateId,
  currencies,
  onCreatedPrice,
  formatPaymentFrequency,
  plugins,
  onSuccessSave,
}: BillableFormProps<T>) => {
  const [confirmableMode, setConfirmableMode] = useState<ConfirmableData | undefined>()
  const [priceFormState, setPriceFormState] = useState<PriceFormState>({ open: false })
  const [pluginRegistry, assignPlugin] = useState<Record<string, SaveCallbackData>>({})
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<T | UnsupportedBillablePrice>({
    values: billablePrice,
  })

  const submitForm = () => {
    setConfirmableMode(undefined)

    setIsLoading(true)

    const promises = Object.values(pluginRegistry).map(({ save }) => save())

    Promise.all(promises)
      .then(onSuccessSave)
      .finally(() => setIsLoading(false))
  }

  const handleSubmit = () => {
    if (!billablePrice) return

    if (Confirmable) {
      return setConfirmableMode({
        type: 'save',
        data: undefined,
      })
    }

    submitForm()
  }

  const deleteBillable = (billableId: string) => {
    setConfirmableMode(undefined)
    onDelete(billableId)
  }

  const handleDelete = (billableId: string) => {
    if (!Confirmable) {
      return deleteBillable(billableId)
    }

    setConfirmableMode({
      type: 'delete',
      data: { billableId },
    })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset plugins when we change billable
  useEffect(() => {
    assignPlugin({})
  }, [billablePrice])

  // React has no way of using useCallback for curried method (i need access to plugin.type). useMemo in combination
  // with map achieves the same result as nesting two useCallbacks, which is not recommended
  const registerSaveCallbacks = useMemo(() => {
    const cache = new Map<string, (data: SaveCallbackData) => void>()

    return (plugin: PriceRowPlugin) => {
      const existing = cache.get(plugin.type)

      if (existing) {
        return existing
      }

      const callback = (data: SaveCallbackData) => {
        assignPlugin((prev) => ({ ...prev, [plugin.type]: data }))
      }

      cache.set(plugin.type, callback)

      return callback
    }
  }, [])

  const handleCreatedPrice = () => {
    setPriceFormState({ open: false })
    onCreatedPrice()
  }

  if (isUnsupportedBillablePrice(billablePrice)) {
    return (
      <Stack
        sx={{
          gap: 0.5,
        }}
      >
        <Typography is="h1" role="heading" variant="p2Medium">
          {title}
        </Typography>
        <Typography data-testid="unsupported-price" variant="p4Regular" color="error">
          This billable has a price that is not supported by backoffice
        </Typography>
      </Stack>
    )
  }

  const changes = Object.values(pluginRegistry).flatMap(({ changes }) => changes)
  const RowPlugins = plugins.filter((plugin) => billablePrice && plugin.supports(billablePrice))

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Stack
        sx={{
          gap: 0,
        }}
      >
        <Typography is="h1" role="heading" variant="p2Medium">
          {title}
        </Typography>
        {billablePrice && (
          <Typography
            variant="p4Regular"
            sx={{
              color: 'text.secondary',
            }}
          >
            Billed {formatPaymentFrequency(billablePrice.paymentFrequency)}
          </Typography>
        )}
      </Stack>

      <Stack
        sx={{
          gap: 3,
        }}
      >
        {RowPlugins.map((plugin) => {
          return (
            <plugin.Component
              key={plugin.type}
              price={billablePrice}
              templateId={templateId}
              unit={unit}
              onRegisterSave={registerSaveCallbacks(plugin)}
              additionalPlugins={plugin.additionalPlugins}
            />
          )
        })}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: 2,
            }}
          >
            <Button
              loading={isLoading}
              sx={{ alignSelf: 'baseline' }}
              onClick={form.handleSubmit(handleSubmit)}
              size="medium"
              variant="contained"
              type="submit"
              disabled={changes.length === 0}
            >
              Save
            </Button>
            {billablePrice && (
              <Button
                sx={{ alignSelf: 'baseline' }}
                size="medium"
                variant="text"
                color="error"
                type="button"
                onClick={() => handleDelete(billablePrice.billableId)}
              >
                Delete
              </Button>
            )}
          </Stack>
          <Button type="button" onClick={() => setPriceFormState({ open: true, mode: billablePrice ? 'edit' : 'create' })}>
            {billablePrice ? 'Edit' : 'Add'} price
          </Button>
        </Stack>
      </Stack>
      {Confirmable && confirmableMode?.type === 'delete' && (
        <Confirmable
          open
          title={`Delete ${title}?`}
          actionText="Yes, delete"
          data={undefined}
          changes={[]}
          onConfirm={() => deleteBillable(confirmableMode.data.billableId)}
          onClose={() => setConfirmableMode(undefined)}
        />
      )}
      {Confirmable && confirmableMode?.type === 'save' && (
        <Confirmable
          open
          title="Save changes?"
          actionText="Yes, save"
          data={undefined}
          changes={formatFieldChangeName({ changes, formatter })}
          onConfirm={submitForm}
          onClose={() => setConfirmableMode(undefined)}
        />
      )}
      <CreatePriceForm
        billableId={billableId}
        templateId={templateId}
        onCreatedPrice={handleCreatedPrice}
        plugins={[
          new LinearCreatePricePlugin(),
          new MaxCreatePricePlugin([new LinearCreatePricePlugin()]),
          new MinCreatePricePlugin([new LinearCreatePricePlugin()]),
          new TieredCreatePricePlugin([
            new LinearCreatePricePlugin(),
            new MaxCreatePricePlugin([new LinearCreatePricePlugin()]),
            new MinCreatePricePlugin([new LinearCreatePricePlugin()]),
          ]),
        ]}
        open={priceFormState.open}
        onClose={() => setPriceFormState({ open: false })}
        price={billablePrice}
        currencies={currencies}
        mode={priceFormState.open ? priceFormState.mode : 'create'}
        unit={unit}
      />
    </Stack>
  )
}

type ConfirmableData = { data: undefined; type: 'save' } | { data: { billableId: string }; type: 'delete' }

const formatter = new StringFormatter({
  normalizers: [
    new StringFormatter.normalizers.camelCase(),
    new StringFormatter.normalizers.enum(),
    new StringFormatter.normalizers.whitespace(),
    new StringFormatter.normalizers.all(),
  ],
  format: new StringFormatter.formats.capitalized(),
})

const noop = () => {}
